// Two-layer animator over split clips (see animLoader: .L legs / .U upper).
//
// Lower layer winner:  fullShot.L  || base.L
// Upper layer winner:  fullShot.U  || upperShot.U || hold.U || base.U
//
// base      — looping locomotion (both halves, kept in sync)
// hold      — persistent upper loop: weapon carry / aim / block
// upperShot — one-shot upper: strikes, gunfire, reloads (legs keep moving)
// fullShot  — one-shot full body: kicks, rolls, transitions, deaths
//
// All active groups crossfade by weight; Babylon weight-averages animations
// that target the same node. Base groups keep playing at ~0 weight while
// overridden so the walk cycle stays in phase when control returns.

const FADE = 0.14;

export class Animator {
  constructor(clips) {
    this.clips = clips;
    this.base = null;      // { name, clip, rate }
    this.hold = null;      // { name, clip }
    this.upperShot = null; // { name, clip }
    this.fullShot = null;  // { name, clip }
    this.dead = false;
    this.lower = new Map(); // group -> { w }
    this.upper = new Map();
    this._gen = 0;          // bumped per one-shot to invalidate stale onEnd
  }

  has(name) { return !!this.clips[name]; }
  dur(name) { return this.clips[name]?.dur ?? 0.5; }

  // rolling animation log (dev tool — see ui/animlog.js): every clip change per
  // layer is recorded so we can watch what the character actually plays.
  _rec(layer, name, rate = 1) {
    const t = typeof performance !== 'undefined' ? performance.now() : 0;
    const e = { t, layer, name: name ?? '—', rate: +rate.toFixed(2) };
    (this.events ||= []).push(e);
    if (this.events.length > 240) this.events.shift();
    this.onAnim?.(e);
  }

  setBase(name, rate = 1) {
    const clip = this.clips[name];
    if (!clip) return;
    if (this.base?.clip === clip) {
      if (this.base.rate !== rate) {
        this.base.rate = rate;
        clip.L.speedRatio = rate; clip.U.speedRatio = rate;
      }
      return;
    }
    this.base = { name, clip, rate };
    this._rec('base', name, rate);
    clip.L.start(true, rate); clip.L.setWeightForAllAnimatables(0.001);
    clip.U.start(true, rate); clip.U.setWeightForAllAnimatables(0.001);
    if (!this.lower.has(clip.L)) this.lower.set(clip.L, { w: 0.001 });
    if (!this.upper.has(clip.U)) this.upper.set(clip.U, { w: 0.001 });
  }

  setHold(name) {
    if ((this.hold?.name ?? null) === name) return;
    const clip = name ? this.clips[name] : null;
    this.hold = clip ? { name, clip } : null;
    this._rec('hold', name);
    if (clip && !this.upper.has(clip.U)) {
      clip.U.start(true, 1);
      clip.U.setWeightForAllAnimatables(0.001);
      this.upper.set(clip.U, { w: 0.001 });
    }
  }

  playUpper(name, rate = 1, onDone) {
    const clip = this.clips[name];
    if (!clip) { onDone?.(); return 0; }
    const gen = ++this._gen;
    this._rec('upper', name, rate);
    if (this.upperShot && this.upperShot.clip !== clip) this.upperShot.clip.U.stop();
    clip.U.stop();
    clip.U.start(false, rate);
    if (!this.upper.has(clip.U)) this.upper.set(clip.U, { w: 0.001 });
    this.upperShot = { name, clip };
    clip.U.onAnimationGroupEndObservable.addOnce(() => {
      if (this._gen === gen) this.upperShot = null;
      onDone?.();
    });
    return clip.dur / rate;
  }

  playFull(name, rate = 1, onDone, fade = FADE) {
    const clip = this.clips[name];
    if (!clip) { onDone?.(); return 0; }
    const gen = ++this._gen;
    this._rec('full', name, rate);
    if (this.fullShot && this.fullShot.clip !== clip) {
      this.fullShot.clip.L.stop(); this.fullShot.clip.U.stop();
    }
    clip.L.stop(); clip.U.stop();
    clip.L.start(false, rate); clip.U.start(false, rate);
    if (!this.lower.has(clip.L)) this.lower.set(clip.L, { w: 0.001 });
    if (!this.upper.has(clip.U)) this.upper.set(clip.U, { w: 0.001 });
    this.fullShot = { name, clip, fade };
    clip.L.onAnimationGroupEndObservable.addOnce(() => {
      if (this._gen === gen) this.fullShot = null;
      onDone?.();
    });
    return clip.dur / rate;
  }

  cancelShots() {
    this._gen++; // invalidate pending onEnd callbacks BEFORE stop() fires them
    if (this.upperShot) { const s = this.upperShot; this.upperShot = null; s.clip.U.stop(); }
    if (this.fullShot) { const s = this.fullShot; this.fullShot = null; s.clip.L.stop(); s.clip.U.stop(); }
  }

  die(name) {
    this.dead = true;
    this.setHold(null);
    this.cancelShots();
    const clip = this.clips[name] || this.clips.death;
    if (!clip) return;
    this.playFull(name in this.clips ? name : 'death', 1, null, 0.08);
    // hold the corpse pose: freeze both halves on their last frame
    const freeze = (g) => {
      g.start(false, 1, Math.max(g.from, g.to - 0.01), g.to);
      g.pause();
      g.setWeightForAllAnimatables(1);
    };
    clip.L.onAnimationGroupEndObservable.addOnce(() => { if (this.dead) { freeze(clip.L); freeze(clip.U); } });
    // orphan playFull's auto-clear and keep the shot marked active so the
    // base loop never resumes under the corpse
    this._gen++;
    this.fullShot = { name, clip, fade: 0.08, dead: true };
  }

  revive() {
    this.dead = false;
    this.cancelShots();
  }

  get busyFull() { return !!this.fullShot; }
  get busyUpper() { return !!this.upperShot || !!this.fullShot; }

  update(dt) {
    const lowerWin = this.fullShot?.clip.L || this.base?.clip.L || null;
    const upperWin = this.fullShot?.clip.U || this.upperShot?.clip.U || this.hold?.clip.U || this.base?.clip.U || null;
    this._layer(this.lower, lowerWin, this.base?.clip.L, dt);
    this._layer(this.upper, upperWin, this.base?.clip.U, dt);
  }

  _layer(map, winner, baseGroup, dt) {
    for (const [g, e] of map) {
      const target = g === winner ? 1 : 0;
      const step = dt / (this.fullShot?.fade || FADE);
      e.w = target > e.w ? Math.min(target, e.w + step) : Math.max(target, e.w - step);
      if (e.w <= 0.001 && g !== winner) {
        if (g === baseGroup || (this.hold && g === this.hold.clip.U)) {
          e.w = 0.001; // keep loops running silently to preserve phase
          g.setWeightForAllAnimatables(0.001);
        } else {
          g.stop(); map.delete(g);
        }
      } else {
        g.setWeightForAllAnimatables(Math.max(e.w, 0.001));
      }
    }
  }
}
